import { FormattedMessage, useIntl } from "react-intl";
import * as Sentry from "@sentry/react";

import { IChoice } from "@/modules/assessments/types/test.interface";

import { TestElementMode } from "@/shared/components/candidate/scenes/TestElement/TestElement";
import { Likert5Choices, likert5ByChoice } from "@/shared/constants/test-elements.constants";

import RadioGroup from "@/ui-components/testElementComponents/RadioGroup/RadioGroup";

import style from "./style.module.css";

export interface ILikert5Group {
  id: number;
  mode?: TestElementMode;
  disable?: boolean;
  choices: IChoice[];
  currentAnswer: string | boolean | undefined;
  onChange?: (e: any) => void;
}

export default function Likert5Group({ id, mode, disable = false, currentAnswer, choices, onChange }: ILikert5Group) {
  const intl = useIntl();
  const isAdminView: boolean = mode === TestElementMode.Admin;

  // Only the canonical 1-5 values have a label; anything else means the element is malformed.
  const invalidChoices = choices.filter(({ choice }) => !likert5ByChoice[Number(choice)]);

  // An admin is here to diagnose the element, so they still get the raw values below. A candidate
  // is being scored by it: rendering an unlabeled choice would let them answer a question whose
  // scale we can't interpret, quietly corrupting the scores. Fail the question visibly instead.
  if (!isAdminView && invalidChoices.length > 0) {
    Sentry.captureMessage(
      `Likert5 element ${id} has choices outside the canonical 1-5 set: ${invalidChoices
        .map(({ choice }) => String(choice))
        .join(", ")}`,
    );
    return (
      <p className={style.error}>
        <FormattedMessage
          id="candidate.errors.invalidQuestion"
          defaultMessage="There's a problem with this question and it can't be answered. Please contact support."
        />
      </p>
    );
  }

  return (
    <RadioGroup
      id={id}
      mode={mode}
      disable={disable || isAdminView}
      onChange={onChange}
      currentAnswer={currentAnswer}
      choices={choices.map(({ choice, ...rest }) => {
        const message = likert5ByChoice[Number(choice)];
        return {
          ...rest,
          choice,
          // In admin view an unlabeled choice falls through to the raw value in RadioGroup, which
          // is the existing (and intended) way a malformed element surfaces to an admin.
          customName: isAdminView ? Likert5Choices[Number(choice)] : intl.formatMessage(message),
        };
      })}
    />
  );
}
